// @flow
/** @jsx jsx */

import Button from '@zapier/common-components/src/components/Button';
import Icon from '@zapier/common-components/src/components/Icon';
import Number from '@zapier/common-components/src/components/Number';
import Text from '@zapier/common-components/src/components/Text';
import { css, jsx } from '@emotion/core';

type Props = {
  canDecrement: boolean,
  canIncrement: boolean,
  endIndex?: number,
  isFaded?: boolean,
  isLoading?: boolean,
  onNextPage: () => void,
  onPrevPage: () => void,
  startIndex: number,
  totalCount: number,
};

const iconWrapperCss = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  padding: '0 12px',
};

const infoTextCss = {
  minWidth: 100,
};

const loadingBlockCss = {
  width: 100,
};

const paginationCss = (props: Props) =>
  css(
    {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      opacity: 1,
      textAlign: 'center',
      transition: 'opacity 0.125s',
    },
    props.isFaded && {
      opacity: 0.5,
    }
  );

const Pagination = (props: Props) => {
  const {
    canDecrement,
    canIncrement,
    endIndex,
    isLoading,
    onNextPage,
    onPrevPage,
    startIndex,
    totalCount,
  } = props;

  return (
    <nav
      aria-label="Pagination Navigation"
      css={paginationCss(props)}
      role="navigation"
    >
      <span css={iconWrapperCss}>
        <Button
          backgroundType="clear"
          isDisabled={isLoading || !canDecrement}
          label="Previous Page"
          onClick={onPrevPage}
        >
          <Icon
            color={!isLoading && canDecrement ? 'deepSpace' : 'steel'}
            name="arrow-left"
          />
        </Button>
      </span>
      {isLoading ? (
        <span aria-label="Loading" css={loadingBlockCss} />
      ) : (
        <span css={infoTextCss}>
          <Text color="batmobile" type="body">
            <Number>{startIndex + 1}</Number>
            {endIndex !== undefined && (
              <span>
                -<Number>{endIndex + 1}</Number>
              </span>
            )}{' '}
            {totalCount && (
              <span>
                (of <Number>{totalCount}</Number>)
              </span>
            )}
          </Text>
        </span>
      )}
      <span css={iconWrapperCss}>
        <Button
          backgroundType="clear"
          isDisabled={isLoading || !canIncrement}
          label="Next Page"
          onClick={onNextPage}
        >
          <Icon
            color={!isLoading && canIncrement ? 'deepSpace' : 'steel'}
            name="arrow-right"
          />
        </Button>
      </span>
    </nav>
  );
};

export default Pagination;
